'use client'

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/api'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { useAgentAuth } from '@/stores/auth'
import { useNotifications } from '@/lib/notifications'
import type { Article, ArticlePayload, ArticleUpdatePayload } from '@/types'
import { useDebounce } from '@/hooks/use-api'
import {
  buildPermissionSet,
  hasAnyPermission,
  PERMISSIONS,
} from '@/lib/permissions'
import { ShieldAlert } from 'lucide-react'

const DEFAULT_PAGE_SIZE = 10

interface FormState {
  title: string
  summary: string
  content: string
  tagsInput: string
  questions: string[]
  isActive: boolean
}

const emptyFormState: FormState = {
  title: '',
  summary: '',
  content: '',
  tagsInput: '',
  questions: [],
  isActive: true,
}

function normalizeTagsInput(value: string): string[] {
  return value
    .split(',')
    .map(tag => tag.trim())
    .filter(tag => tag.length > 0)
}

function hydrateFormState(article?: Article | null): FormState {
  if (!article) {
    return { ...emptyFormState }
  }

  return {
    title: article.title,
    summary: article.summary ?? '',
    content: article.content,
    tagsInput: (article.tags ?? []).join(', '),
    questions: article.questions?.map(question => question.question) ?? [],
    isActive: article.isActive,
  }
}

export function AgentArticlesPanel() {
  const { user } = useAgentAuth()
  const notifications = useNotifications()
  const queryClient = useQueryClient()

  const permissionSet = useMemo(
    () => buildPermissionSet(user?.permissions),
    [user?.permissions]
  )

  const canCreateArticles = hasAnyPermission(permissionSet, [
    PERMISSIONS.ARTICLE_CREATE,
    PERMISSIONS.ANY,
  ])
  const canUpdateAllArticles = hasAnyPermission(permissionSet, [
    PERMISSIONS.ARTICLE_UPDATE_ALL,
    PERMISSIONS.ANY,
  ])
  const canUpdateOwnArticles = hasAnyPermission(permissionSet, [
    PERMISSIONS.ARTICLE_UPDATE_OWN,
    PERMISSIONS.ANY,
  ])
  const canDeleteArticles = hasAnyPermission(permissionSet, [
    PERMISSIONS.ARTICLE_DELETE,
    PERMISSIONS.ANY,
  ])

  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [selectedArticleId, setSelectedArticleId] = useState<number | null>(
    null
  )
  const [activeArticle, setActiveArticle] = useState<Article | null>(null)
  const [formState, setFormState] = useState<FormState>({ ...emptyFormState })
  const [pendingQuestion, setPendingQuestion] = useState('')
  const debouncedSearchTerm = useDebounce(searchTerm, 300)
  const normalizedSearch = useMemo(
    () => debouncedSearchTerm.trim(),
    [debouncedSearchTerm]
  )

  const listParams = useMemo(
    () => ({
      search: normalizedSearch.length > 0 ? normalizedSearch : undefined,
      page,
      pageSize: DEFAULT_PAGE_SIZE,
    }),
    [normalizedSearch, page]
  )

  const articlesQuery = useQuery({
    queryKey: queryKeys.agentArticles.list(listParams),
    queryFn: async () => {
      const response = await api.agent.listArticles({
        search: listParams.search,
        page: listParams.page,
        pageSize: listParams.pageSize,
      })

      if (!response.success) {
        throw new Error(
          response.error || 'Failed to load saved articles. Please try again.'
        )
      }

      return response
    },
    keepPreviousData: true,
    onSuccess: response => {
      const pageSize =
        response.pagination?.pageSize && response.pagination.pageSize > 0
          ? response.pagination.pageSize
          : DEFAULT_PAGE_SIZE
      const totalItems =
        response.pagination?.total ?? response.data?.length ?? 0
      const computedTotalPages = Math.max(1, Math.ceil(totalItems / pageSize))

      if (page > computedTotalPages) {
        setPage(computedTotalPages)
        return
      }

      if (
        page > 1 &&
        response.data?.length === 0 &&
        computedTotalPages >= 1 &&
        page !== computedTotalPages
      ) {
        setPage(computedTotalPages)
      }
    },
  })

  const articles = articlesQuery.data?.data ?? []
  const pagination = articlesQuery.data?.pagination
  const effectivePageSize =
    pagination?.pageSize && pagination.pageSize > 0
      ? pagination.pageSize
      : DEFAULT_PAGE_SIZE
  const totalPages = pagination
    ? Math.max(1, Math.ceil(pagination.total / effectivePageSize))
    : null
  const disableNext =
    pagination && totalPages !== null
      ? pagination.page >= totalPages
      : articles.length < effectivePageSize

  const selectArticle = (article: Article | null) => {
    if (!article) {
      setSelectedArticleId(null)
      setFormState({ ...emptyFormState })
      setActiveArticle(null)
      return
    }

    setSelectedArticleId(article.id)
    setActiveArticle(article)
    setFormState(hydrateFormState(article))
  }

  const resetToNewArticle = () => {
    selectArticle(null)
  }

  const createMutation = useMutation({
    mutationFn: async (payload: ArticlePayload) => {
      const response = await api.agent.createArticle(payload)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Unable to create article')
      }
      return response.data
    },
    onSuccess: article => {
      notifications.showSuccess(
        'Article created',
        'The saved reply has been added to your library.'
      )
      setPage(1)
      queryClient.invalidateQueries({ queryKey: queryKeys.agentArticles.all })
      selectArticle(article)
    },
    onError: (error: unknown) => {
      notifications.showError(
        'Create failed',
        error instanceof Error ? error.message : 'Unable to create article'
      )
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({
      articleId,
      payload,
    }: {
      articleId: number
      payload: ArticleUpdatePayload
    }) => {
      const response = await api.agent.updateArticle(articleId, payload)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Unable to update article')
      }
      return response.data
    },
    onSuccess: updated => {
      notifications.showSuccess(
        'Article updated',
        'Changes saved successfully.'
      )
      queryClient.invalidateQueries({ queryKey: queryKeys.agentArticles.all })
      selectArticle(updated)
    },
    onError: (error: unknown) => {
      notifications.showError(
        'Update failed',
        error instanceof Error ? error.message : 'Unable to update article'
      )
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (articleId: number) => {
      const response = await api.agent.deleteArticle(articleId)
      if (!response.success) {
        const message =
          response.error ||
          (typeof response.message === 'string' ? response.message : null) ||
          'Unable to delete article'
        throw new Error(message)
      }
    },
    onSuccess: () => {
      notifications.showSuccess(
        'Article deleted',
        'The article has been removed.'
      )
      queryClient.invalidateQueries({ queryKey: queryKeys.agentArticles.all })
      resetToNewArticle()
    },
    onError: (error: unknown) => {
      notifications.showError(
        'Delete failed',
        error instanceof Error ? error.message : 'Unable to delete article'
      )
    },
  })

  const handleSubmit = () => {
    if (!formState.title.trim()) {
      notifications.showError(
        'Missing title',
        'Please provide a title before saving.'
      )
      return
    }
    if (!formState.content.trim()) {
      notifications.showError('Missing content', 'Content cannot be empty.')
      return
    }

    const trimmedSummary = formState.summary.trim()

    const payloadBase = {
      title: formState.title.trim(),
      summary: trimmedSummary.length > 0 ? trimmedSummary : null,
      content: formState.content,
      tags: normalizeTagsInput(formState.tagsInput),
      questions: formState.questions
        .map(question => question.trim())
        .filter(question => question.length > 0),
      isActive: formState.isActive,
    }

    if (selectedArticleId) {
      if (!canEditSelectedArticle) {
        notifications.showError(
          'Permission denied',
          'You do not have access to update this article.'
        )
        return
      }
      updateMutation.mutate({
        articleId: selectedArticleId,
        payload: payloadBase,
      })
    } else {
      if (!canCreateArticles) {
        notifications.showError(
          'Permission denied',
          'You do not have access to create articles.'
        )
        return
      }
      createMutation.mutate(payloadBase)
    }
  }

  const handleDelete = () => {
    if (!selectedArticleId) {
      return
    }
    if (!canDeleteSelectedArticle) {
      notifications.showError(
        'Permission denied',
        'You do not have access to delete this article.'
      )
      return
    }
    deleteMutation.mutate(selectedArticleId)
  }

  const handleQuestionUpdate = (index: number, value: string) => {
    if (formReadOnly) {
      return
    }
    setFormState(prev => ({
      ...prev,
      questions: prev.questions.map((question, idx) =>
        idx === index ? value : question
      ),
    }))
  }

  const handleQuestionRemove = (index: number) => {
    if (formReadOnly) {
      return
    }
    setFormState(prev => ({
      ...prev,
      questions: prev.questions.filter((_, idx) => idx !== index),
    }))
  }

  const handleQuestionAdd = () => {
    if (formReadOnly) {
      return
    }
    const trimmed = pendingQuestion.trim()
    if (!trimmed) {
      return
    }
    setFormState(prev => ({
      ...prev,
      questions: [...prev.questions, trimmed],
    }))
    setPendingQuestion('')
  }

  const isMutating =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending

  const canEditSelectedArticle = (() => {
    if (selectedArticleId === null) {
      return canCreateArticles
    }

    if (!activeArticle) {
      return canUpdateAllArticles
    }

    if (canUpdateAllArticles) {
      return true
    }

    if (canUpdateOwnArticles && user?.id != null) {
      return activeArticle.createdById === user.id
    }

    return false
  })()

  const canDeleteSelectedArticle = (() => {
    if (selectedArticleId === null || !canDeleteArticles) {
      return false
    }

    if (canUpdateAllArticles) {
      return true
    }

    if (canUpdateOwnArticles && user?.id != null && activeArticle) {
      return activeArticle.createdById === user.id
    }

    return false
  })()

  const editRestrictionNotice = (() => {
    if (selectedArticleId === null || canEditSelectedArticle) {
      return null
    }

    if (!canUpdateAllArticles && !canUpdateOwnArticles) {
      return {
        title: 'View-only access',
        description:
          'Your role does not include article update permissions. Reach out to an administrator if you need to make changes.',
      }
    }

    if (
      canUpdateOwnArticles &&
      !canUpdateAllArticles &&
      activeArticle &&
      user?.id != null &&
      activeArticle.createdById !== user.id
    ) {
      return {
        title: 'Limited to your own articles',
        description:
          'You can only update or delete articles you created. This article belongs to another teammate.',
      }
    }

    return {
      title: 'Update restricted',
      description: 'You do not have permission to modify this article.',
    }
  })()

  const formReadOnly = !canEditSelectedArticle || isMutating

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <Input
            value={searchTerm}
            onChange={event => {
              setSearchTerm(event.target.value)
              setPage(1)
            }}
            className="w-64"
            placeholder="Search articles..."
          />
          <Button
            variant="outline"
            onClick={() => articlesQuery.refetch()}
            disabled={articlesQuery.isFetching}
          >
            Refresh
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {articlesQuery.isLoading
              ? 'Loading articles...'
              : `${pagination?.total ?? articles.length} saved replies`}
          </span>
          <Button
            onClick={resetToNewArticle}
            variant="default"
            disabled={!canCreateArticles || isMutating}
          >
            New Article
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(260px,320px)_1fr]">
        <div className="rounded-lg border bg-white">
          <div className="border-b px-4 py-3">
            <h3 className="text-sm font-semibold text-muted-foreground">
              Library
            </h3>
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            {articlesQuery.isLoading && (
              <p className="px-4 py-6 text-sm text-muted-foreground">
                Loading articles...
              </p>
            )}
            {articlesQuery.isError && !articlesQuery.isLoading && (
              <p className="px-4 py-6 text-sm text-red-600">
                {(articlesQuery.error as Error)?.message ??
                  'Unable to load articles. Please try again.'}
              </p>
            )}
            {!articlesQuery.isLoading &&
              !articlesQuery.isError &&
              articles.length === 0 && (
                <p className="px-4 py-6 text-sm text-muted-foreground">
                  No articles found. Save common replies to reuse them quickly.
                </p>
              )}
            <ul className="divide-y">
              {articles.map(article => {
                const isSelected = article.id === selectedArticleId
                return (
                  <li key={article.id}>
                    <button
                      type="button"
                      onClick={() => selectArticle(article)}
                      className={`w-full px-4 py-3 text-left transition ${
                        isSelected
                          ? 'bg-blue-50 border-l-4 border-blue-500'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-medium text-sm text-gray-900 line-clamp-1">
                            {article.title}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                            {article.summary || article.content.slice(0, 120)}
                          </p>
                        </div>
                        {!article.isActive && (
                          <Badge variant="outline">Inactive</Badge>
                        )}
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
          <div className="border-t px-4 py-3 flex items-center justify-between text-xs text-muted-foreground">
            <Button
              variant="ghost"
              size="sm"
              disabled={page <= 1 || articlesQuery.isFetching}
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
            >
              Previous
            </Button>
            <span>
              Page {pagination?.page ?? page} of {totalPages ?? '—'}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={articlesQuery.isFetching || disableNext}
              onClick={() => setPage(prev => prev + 1)}
            >
              Next
            </Button>
          </div>
        </div>

        <div className="rounded-lg border bg-white p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">
                {selectedArticleId ? 'Edit Article' : 'New Article'}
              </h3>
              <p className="text-sm text-muted-foreground">
                Capture polished replies with starter questions so teammates can
                find them quickly.
              </p>
            </div>
            {selectedArticleId && canDeleteSelectedArticle && (
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={isMutating || !canDeleteSelectedArticle}
              >
                Delete
              </Button>
            )}
          </div>

          {!canCreateArticles && selectedArticleId === null && (
            <Alert className="border-amber-200 bg-amber-50 text-amber-900">
              <ShieldAlert className="h-4 w-4 text-amber-600" />
              <AlertTitle>Creation restricted</AlertTitle>
              <AlertDescription>
                You do not have permission to create new articles.
              </AlertDescription>
            </Alert>
          )}

          {editRestrictionNotice && (
            <Alert className="border-amber-200 bg-amber-50 text-amber-900">
              <ShieldAlert className="h-4 w-4 text-amber-600" />
              <AlertTitle>{editRestrictionNotice.title}</AlertTitle>
              <AlertDescription>
                {editRestrictionNotice.description}
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="article-title">Title</Label>
              <Input
                id="article-title"
                value={formState.title}
                onChange={event =>
                  setFormState(prev => ({ ...prev, title: event.target.value }))
                }
                placeholder="Example: Greeting a returning customer"
                disabled={formReadOnly}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="article-summary">Summary</Label>
              <Input
                id="article-summary"
                value={formState.summary}
                onChange={event =>
                  setFormState(prev => ({
                    ...prev,
                    summary: event.target.value,
                  }))
                }
                placeholder="Optional short description"
                disabled={formReadOnly}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="article-content">Content</Label>
              <Textarea
                id="article-content"
                rows={8}
                value={formState.content}
                onChange={event =>
                  setFormState(prev => ({
                    ...prev,
                    content: event.target.value,
                  }))
                }
                placeholder="Main reply that will be inserted into chat"
                disabled={formReadOnly}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="article-tags">Tags</Label>
              <Input
                id="article-tags"
                value={formState.tagsInput}
                onChange={event =>
                  setFormState(prev => ({
                    ...prev,
                    tagsInput: event.target.value,
                  }))
                }
                placeholder="Comma separated keywords (billing, onboarding, refund)"
                disabled={formReadOnly}
              />
            </div>

            <div className="grid gap-2">
              <Label>Sample questions</Label>
              <p className="text-xs text-muted-foreground">
                Add the phrases customers usually ask so the article appears in
                keyword searches.
              </p>
              <div className="space-y-2">
                {formState.questions.map((question, index) => (
                  <div
                    key={`question-${index}`}
                    className="flex items-center gap-2"
                  >
                    <Input
                      value={question}
                      onChange={event =>
                        handleQuestionUpdate(index, event.target.value)
                      }
                      placeholder="How do I reset my password?"
                      disabled={formReadOnly}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleQuestionRemove(index)}
                      disabled={formReadOnly}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={pendingQuestion}
                  onChange={event => setPendingQuestion(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      handleQuestionAdd()
                    }
                  }}
                  placeholder="Add another common question"
                  disabled={formReadOnly}
                />
                <Button
                  variant="outline"
                  onClick={handleQuestionAdd}
                  disabled={formReadOnly || pendingQuestion.trim().length === 0}
                >
                  Add
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="article-active"
                checked={formState.isActive}
                onCheckedChange={checked =>
                  setFormState(prev => ({
                    ...prev,
                    isActive: Boolean(checked),
                  }))
                }
                disabled={formReadOnly}
              />
              <Label htmlFor="article-active">
                Make article available for agents
              </Label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            <Button
              variant="ghost"
              onClick={resetToNewArticle}
              disabled={isMutating || !canCreateArticles}
            >
              Clear
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isMutating || !canEditSelectedArticle}
            >
              {selectedArticleId ? 'Update Article' : 'Create Article'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AgentArticlesPanel
